import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Trash2, RotateCcw, Loader2, Sparkles, Bot } from "lucide-react";

export default function ErrosPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prof, setProf] = useState({ open: false, text: "", loading: false });

  const load = useCallback(() => {
    setLoading(true);
    api.get("/errors").then((r) => setErrors(r.data.errors))
      .catch((e) => toast.error(formatError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/errors/${id}`);
      setErrors((e) => e.filter((x) => x.id !== id));
      toast.success("Removida do caderno de erros");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const explain = async (questionId) => {
    if (!user?.is_premium) {
      navigate("/premium");
      return;
    }
    setProf({ open: true, text: "", loading: true });
    try {
      const { data } = await api.post("/ia/professor", { question_id: questionId, mode: "explicar" });
      setProf({ open: true, text: data.resposta, loading: false });
    } catch (e) {
      setProf({ open: true, text: formatError(e.response?.data?.detail) || "Erro ao consultar o Professor IA.", loading: false });
    }
  };

  return (
    <div data-testid="erros-page" className="mx-auto max-w-3xl space-y-4 px-4 py-6 sm:px-6 sm:py-8">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-red-500/15 text-red-400"><AlertTriangle className="h-5 w-5" /></span>
        <div>
          <h1 className="text-2xl font-extrabold text-white">Caderno de Erros</h1>
          <p className="text-sm text-slate-400">Suas questões erradas ficam aqui para revisão inteligente.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-emerald-400" /></div>
      ) : errors.length === 0 ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-10 text-center text-slate-400" data-testid="erros-empty">
          Nenhum erro registrado ainda. Continue praticando! 💪
        </div>
      ) : (
        errors.map((e) => (
          <div key={e.id} data-testid="erro-card" className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
            <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px] font-semibold">
              <span className="rounded bg-slate-800 px-2 py-0.5 text-slate-300">{e.disciplina}</span>
              {e.banca && <span className="rounded bg-slate-800 px-2 py-0.5 text-slate-400">{e.banca}</span>}
              <span className="rounded bg-red-500/15 px-2 py-0.5 text-red-300">errou {e.err_count}x</span>
            </div>
            <p className="text-sm text-slate-100">{e.question?.enunciado || "(questão indisponível)"}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={() => navigate("/questoes")} data-testid="erro-refazer"
                className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-600">
                <RotateCcw className="h-3.5 w-3.5" /> Refazer
              </button>
              <button onClick={() => explain(e.question_id)} data-testid="erro-professor"
                className="flex items-center gap-1.5 rounded-lg bg-indigo-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-indigo-600">
                <Sparkles className="h-3.5 w-3.5" /> Professor IA
              </button>
              <button onClick={() => remove(e.id)} data-testid="erro-remover"
                className="flex items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-bold text-slate-300 hover:bg-slate-700">
                <Trash2 className="h-3.5 w-3.5" /> Remover
              </button>
            </div>
          </div>
        ))
      )}

      <Dialog open={prof.open} onOpenChange={(o) => setProf((p) => ({ ...p, open: o }))}>
        <DialogContent className="max-h-[80vh] overflow-y-auto border-slate-700 bg-slate-900 text-white sm:max-w-lg">
          <DialogTitle className="flex items-center gap-2 text-indigo-300"><Bot className="h-5 w-5" /> Professor ApoiaConcursos IA</DialogTitle>
          {prof.loading ? (
            <div className="flex items-center gap-2 py-8 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Pensando…</div>
          ) : (
            <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-200" data-testid="professor-response">{prof.text}</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
